import { IVector3 } from './IVector3.js';
import { IVector4 } from './IVector4.js';
/**
 * Utility class for creating and manipulating `IMatrix4` objects.
 */
export class IMatrix4 {
    /**
     * Creates a new identity matrix
     * @returns Anonymous object `IMatrix4`
     */
    constructor() {
        return [
            { x: 1, y: 0, z: 0, w: 0 },
            { x: 0, y: 1, z: 0, w: 0 },
            { x: 0, y: 0, z: 1, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Type guard to check if the provided object is applicable to the `IMatrix4` interface.
     * @returns Type assertion if the object is applicable to the IMatrix4.
     */
    static is(value) {
        const matrix = value;
        return Array.isArray(matrix) && IVector4.is(matrix[0]) && IVector4.is(matrix[1]) && IVector4.is(matrix[2]) && IVector4.is(matrix[3]);
    }
    /**
     * Creates an identity matrix. Identical to using the IMatrix4 constructor
     * @returns Anonymous object `IMatrix4`
     */
    static Identity() {
        return new IMatrix4();
    }
    /**
     * Creates a translation matrix from `x`, `y`, `z` displacements.
     * @returns Anonymous object `IMatrix4`
     */
    static Translation(x, y, z) {
        return [
            { x: 1, y: 0, z: 0, w: 0 },
            { x: 0, y: 1, z: 0, w: 0 },
            { x: 0, y: 0, z: 1, w: 0 },
            { x: x, y: y, z: z, w: 1 }
        ];
    }
    /**
     * Creates a scale matrix from `x`, `y`, `z` scale factors.
     * @returns Anonymous object `IMatrix4`
     */
    static Scale(x, y, z) {
        return [
            { x: x, y: 0, z: 0, w: 0 },
            { x: 0, y: y, z: 0, w: 0 },
            { x: 0, y: 0, z: z, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Creates a rotation matrix around the X-axis.
     * @returns Anonymous object `IMatrix4`
     */
    static Rotation_X(angle_in_radians) {
        const s = Math.sin(angle_in_radians);
        const c = Math.cos(angle_in_radians);
        return [
            { x: 1, y: 0, z: 0, w: 0 },
            { x: 0, y: c, z: -s, w: 0 },
            { x: 0, y: s, z: c, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Creates a rotation matrix around the Y-axis.
     * @returns Anonymous object `IMatrix4`
     */
    static Rotation_Y(angle_in_radians) {
        const s = Math.sin(angle_in_radians);
        const c = Math.cos(angle_in_radians);
        return [
            { x: c, y: 0, z: s, w: 0 },
            { x: 0, y: 1, z: 0, w: 0 },
            { x: -s, y: 0, z: c, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Creates a rotation matrix around the Z-axis.
     * @returns Anonymous object `IMatrix4`
     */
    static Rotation_Z(angle_in_radians) {
        const s = Math.sin(angle_in_radians);
        const c = Math.cos(angle_in_radians);
        return [
            { x: c, y: -s, z: 0, w: 0 },
            { x: s, y: c, z: 0, w: 0 },
            { x: 0, y: 0, z: 1, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Extracts a rotation part from a matrix.
     * @returns Anonymous object `IMatrix4`
     */
    static extract_rotation_matrix(matrix) {
        const row0 = IVector3.normalize(matrix[0]);
        const row1 = IVector3.normalize(matrix[1]);
        const row2 = IVector3.normalize(matrix[2]);
        return [
            { x: row0.x, y: row0.y, z: row0.z, w: 0 },
            { x: row1.x, y: row1.y, z: row1.z, w: 0 },
            { x: row2.x, y: row2.y, z: row2.z, w: 0 },
            { x: 0, y: 0, z: 0, w: 1 }
        ];
    }
    /**
     * Extracts scaling values from a matrix.
     * @returns A new `IVector3` object.
     */
    static extract_scale(matrix) {
        const scaleX = IVector3.magnitude({ x: matrix[0].x, y: matrix[0].y, z: matrix[0].z });
        const scaleY = IVector3.magnitude({ x: matrix[1].x, y: matrix[1].y, z: matrix[1].z });
        const scaleZ = IVector3.magnitude({ x: matrix[2].x, y: matrix[2].y, z: matrix[2].z });
        return { x: scaleX, y: scaleY, z: scaleZ };
    }
    /**
     * Checks if the provided matrix is an identity matrix.
     * @returns `true` if the matrix is an identity matrix.
     */
    static is_identity(matrix) {
        return matrix[0].x === 1 && matrix[0].y === 0 && matrix[0].z === 0 && matrix[0].w === 0 &&
            matrix[1].x === 0 && matrix[1].y === 1 && matrix[1].z === 0 && matrix[1].w === 0 &&
            matrix[2].x === 0 && matrix[2].y === 0 && matrix[2].z === 1 && matrix[2].w === 0 &&
            matrix[3].x === 0 && matrix[3].y === 0 && matrix[3].z === 0 && matrix[3].w === 1;
    }
    static fill_in_place(matrix, value) {
        for (let i = 0; i < 4; i++) {
            matrix[i].x = value;
            matrix[i].y = value;
            matrix[i].z = value;
            matrix[i].w = value;
        }
        return matrix;
    }
    /**
     * Creates a new `IMatrix4` object as a verbose copy of the provided matrix.
     * @returns A new `IMatrix4` object.
     */
    static copy(matrix) {
        return [
            { x: matrix[0].x, y: matrix[0].y, z: matrix[0].z, w: matrix[0].w },
            { x: matrix[1].x, y: matrix[1].y, z: matrix[1].z, w: matrix[1].w },
            { x: matrix[2].x, y: matrix[2].y, z: matrix[2].z, w: matrix[2].w },
            { x: matrix[3].x, y: matrix[3].y, z: matrix[3].z, w: matrix[3].w }
        ];
    }
    /**
     * Copies the values from one `IMatrix4` object to another.
     * @note This method directly modifies the `result` matrix.
     */
    static copy_to_ref(matrix, result) {
        result[0].x = matrix[0].x;
        result[0].y = matrix[0].y;
        result[0].z = matrix[0].z;
        result[0].w = matrix[0].w;
        result[1].x = matrix[1].x;
        result[1].y = matrix[1].y;
        result[1].z = matrix[1].z;
        result[1].w = matrix[1].w;
        result[2].x = matrix[2].x;
        result[2].y = matrix[2].y;
        result[2].z = matrix[2].z;
        result[2].w = matrix[2].w;
        result[3].x = matrix[3].x;
        result[3].y = matrix[3].y;
        result[3].z = matrix[3].z;
        result[3].w = matrix[3].w;
        return result;
    }
    /**
     * Calculates the determinant of the provided matrix.
     * @returns The determinant of the matrix.
     */
    static determinant(m) {
        const { m11, m12, m13, m14, m21, m22, m23, m24, m31, m32, m33, m34, m41, m42, m43, m44 } = IMatrix4.to_matrix_object(m);
        // Determinant components
        const det_33_44 = m33 * m44 - m34 * m43;
        const det_32_44 = m32 * m44 - m34 * m42;
        const det_32_43 = m32 * m43 - m33 * m42;
        const det_31_44 = m31 * m44 - m34 * m41;
        const det_31_43 = m31 * m43 - m33 * m41;
        const det_31_42 = m31 * m42 - m32 * m41;
        // Cofactor calculations
        const cofact_11 = +(m22 * det_33_44 - m23 * det_32_44 + m24 * det_32_43);
        const cofact_12 = -(m21 * det_33_44 - m23 * det_31_44 + m24 * det_31_43);
        const cofact_13 = +(m21 * det_32_44 - m22 * det_31_44 + m24 * det_31_42);
        const cofact_14 = -(m21 * det_32_43 - m22 * det_31_43 + m23 * det_31_42);
        return m11 * cofact_11 + m12 * cofact_12 + m13 * cofact_13 + m14 * cofact_14;
    }
    /**
     * Inverts the provided matrix.
     * @returns Anonymous object `IMatrix4`
     */
    static inverse(m) {
        const matrix = new IMatrix4();
        return this.inverse_to_ref(m, matrix);
    }
    /**
     * Inverts the provided matrix in place.
     * @note This method directly modifies the matrix.
     * @returns The modified `IMatrix4` object reference.
     */
    static inverse_in_place(m) {
        return this.inverse_to_ref(m, m);
    }
    /**
     * Inverts the provided matrix and writes the result to the `result` matrix.
     * @note This method directly modifies the `result` matrix.
     */
    static inverse_to_ref(m, result) {
        const { m11, m12, m13, m14, m21, m22, m23, m24, m31, m32, m33, m34, m41, m42, m43, m44 } = IMatrix4.to_matrix_object(m);
        // Determinant components
        const det_33_44 = m33 * m44 - m34 * m43;
        const det_32_44 = m32 * m44 - m34 * m42;
        const det_32_43 = m32 * m43 - m33 * m42;
        const det_31_44 = m31 * m44 - m34 * m41;
        const det_31_43 = m31 * m43 - m33 * m41;
        const det_31_42 = m31 * m42 - m32 * m41;
        const det_23_44 = m23 * m44 - m24 * m43;
        const det_22_44 = m22 * m44 - m24 * m42;
        const det_22_43 = m22 * m43 - m23 * m42;
        const det_21_44 = m21 * m44 - m24 * m41;
        const det_21_43 = m21 * m43 - m23 * m41;
        const det_21_42 = m21 * m42 - m22 * m41;
        const det_23_34 = m23 * m34 - m24 * m33;
        const det_22_34 = m22 * m34 - m24 * m32;
        const det_22_33 = m22 * m33 - m23 * m32;
        const det_21_34 = m21 * m34 - m24 * m31;
        const det_21_33 = m21 * m33 - m23 * m31;
        const det_21_32 = m21 * m32 - m22 * m31;
        // Cofactors for m11, m12, m13, m14
        const cofact_11 = +(m22 * det_33_44 - m23 * det_32_44 + m24 * det_32_43);
        const cofact_12 = -(m21 * det_33_44 - m23 * det_31_44 + m24 * det_31_43);
        const cofact_13 = +(m21 * det_32_44 - m22 * det_31_44 + m24 * det_31_42);
        const cofact_14 = -(m21 * det_32_43 - m22 * det_31_43 + m23 * det_31_42);
        const det = m11 * cofact_11 + m12 * cofact_12 + m13 * cofact_13 + m14 * cofact_14;
        if (det === 0) {
            console.warn('Matrix determinant is zero so it could not be inverted');
            return IMatrix4.fill_in_place(result, Number.NaN);
        }
        const det_inv = 1 / det;
        // Cofactors for the inverse matrix
        const cofact_21 = -(m12 * det_33_44 - m13 * det_32_44 + m14 * det_32_43);
        const cofact_22 = +(m11 * det_33_44 - m13 * det_31_44 + m14 * det_31_43);
        const cofact_23 = -(m11 * det_32_44 - m12 * det_31_44 + m14 * det_31_42);
        const cofact_24 = +(m11 * det_32_43 - m12 * det_31_43 + m13 * det_31_42);
        const cofact_31 = +(m12 * det_23_44 - m13 * det_22_44 + m14 * det_22_43);
        const cofact_32 = -(m11 * det_23_44 - m13 * det_21_44 + m14 * det_21_43);
        const cofact_33 = +(m11 * det_22_44 - m12 * det_21_44 + m14 * det_21_42);
        const cofact_34 = -(m11 * det_22_43 - m12 * det_21_43 + m13 * det_21_42);
        const cofact_41 = -(m12 * det_23_34 - m13 * det_22_34 + m14 * det_22_33);
        const cofact_42 = +(m11 * det_23_34 - m13 * det_21_34 + m14 * det_21_33);
        const cofact_43 = -(m11 * det_22_34 - m12 * det_21_34 + m14 * det_21_32);
        const cofact_44 = +(m11 * det_22_33 - m12 * det_21_33 + m13 * det_21_32);
        IMatrix4.from_values_to_ref(cofact_11 * det_inv, cofact_21 * det_inv, cofact_31 * det_inv, cofact_41 * det_inv, cofact_12 * det_inv, cofact_22 * det_inv, cofact_32 * det_inv, cofact_42 * det_inv, cofact_13 * det_inv, cofact_23 * det_inv, cofact_33 * det_inv, cofact_43 * det_inv, cofact_14 * det_inv, cofact_24 * det_inv, cofact_34 * det_inv, cofact_44 * det_inv, result);
        return result;
    }
    /**
     * Calculates the inverse of the provided matrix with the assumption that it is an affine transform with an orthonormal rotation part.
     * Compared to a normal matrix inverse this method is faster & more precise when usable.
     * @returns A new matrix containing the result
     */
    static orthonormal_rotation_affine_transform_inverse(matrix) {
        const result = new IMatrix4();
        return IMatrix4.orthonormal_rotation_affine_transform_inverse_to_ref(matrix, result);
    }
    /**
     * Inverts the provided matrix with the assumption that it is an affine transform with an orthonormal rotation part, and writes the result to the `result` matrix.
     * Compared to a normal matrix inverse this method is faster & more precise when usable.
     * @note This method directly modifies the `result` matrix.
     */
    static orthonormal_rotation_affine_transform_inverse_to_ref(matrix, result) {
        // Store the inverse of the orthonormal rotation matrix
        IMatrix4.from_values_to_ref(matrix[0].x, matrix[1].x, matrix[2].x, 0, matrix[0].y, matrix[1].y, matrix[2].y, 0, matrix[0].z, matrix[1].z, matrix[2].z, 0, 0, 0, 0, 1, result);
        // Calculate the inverse position
        const inverse_position = IMatrix4.transform_vector4(result, matrix[3]);
        result[3].x = -inverse_position.x;
        result[3].y = -inverse_position.y;
        result[3].z = -inverse_position.z;
        return result;
    }
    /**
     * Multiplies two matrices and returns a new matrix.
     * @returns Anonymous object `IMatrix4`
     */
    static multiply(a, b) {
        const result = [
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 },
            { x: 0, y: 0, z: 0, w: 0 }
        ];
        for (let i = 0; i < 4; i++) {
            result[i].x = a[0].x * b[i].x + a[1].x * b[i].y + a[2].x * b[i].z + a[3].x * b[i].w;
            result[i].y = a[0].y * b[i].x + a[1].y * b[i].y + a[2].y * b[i].z + a[3].y * b[i].w;
            result[i].z = a[0].z * b[i].x + a[1].z * b[i].y + a[2].z * b[i].z + a[3].z * b[i].w;
            result[i].w = a[0].w * b[i].x + a[1].w * b[i].y + a[2].w * b[i].z + a[3].w * b[i].w;
        }
        return result;
    }
    static equals(matrix1, matrix2) {
        for (let i = 0; i < 4; i++) {
            if (matrix1[i].x !== matrix2[i].x ||
                matrix1[i].y !== matrix2[i].y ||
                matrix1[i].z !== matrix2[i].z ||
                matrix1[i].w !== matrix2[i].w) {
                return false;
            }
        }
        return true;
    }
    static lerp(a, b, t) {
        return IMatrix4.lerp_to_ref(a, b, t, new IMatrix4());
    }
    static lerp_to_ref(a, b, t, ref) {
        for (let i = 0; i < 4; i++) {
            IVector4.lerp_to_ref(a[i], b[i], t, ref[i]);
        }
        return ref;
    }
    /**
     * Transforms a `IVector3` by a matrix and returns a new vector.
     * Assumes w = 1
     * @returns A new `IVector3` object.
     */
    static transform_vector3(matrix, vector) {
        // We assume w is 1.
        return {
            x: matrix[0].x * vector.x + matrix[1].x * vector.y + matrix[2].x * vector.z + matrix[3].x,
            y: matrix[0].y * vector.x + matrix[1].y * vector.y + matrix[2].y * vector.z + matrix[3].y,
            z: matrix[0].z * vector.x + matrix[1].z * vector.y + matrix[2].z * vector.z + matrix[3].z
        };
    }
    /**
     * Transforms a `IVector3` by a matrix and writes the result into ref.
     * Assumes w = 1
     * @returns ref
     */
    static transform_vector3_to_ref(matrix, vector, ref) {
        // We assume w is 1.
        const x = matrix[0].x * vector.x + matrix[1].x * vector.y + matrix[2].x * vector.z + matrix[3].x;
        const y = matrix[0].y * vector.x + matrix[1].y * vector.y + matrix[2].y * vector.z + matrix[3].y;
        const z = matrix[0].z * vector.x + matrix[1].z * vector.y + matrix[2].z * vector.z + matrix[3].z;
        ref.x = x;
        ref.y = y;
        ref.z = z;
        return ref;
    }
    /**
     * Transforms a `IVector4` by a matrix and returns a new vector.
     * @returns A new `IVector4` object.
     */
    static transform_vector4(matrix, vector) {
        return {
            x: matrix[0].x * vector.x + matrix[1].x * vector.y + matrix[2].x * vector.z + matrix[3].x * vector.w,
            y: matrix[0].y * vector.x + matrix[1].y * vector.y + matrix[2].y * vector.z + matrix[3].y * vector.w,
            z: matrix[0].z * vector.x + matrix[1].z * vector.y + matrix[2].z * vector.z + matrix[3].z * vector.w,
            w: matrix[0].w * vector.x + matrix[1].w * vector.y + matrix[2].w * vector.z + matrix[3].w * vector.w
        };
    }
    static transform_vector4_to_ref(matrix, vector, result) {
        const x = matrix[0].x * vector.x + matrix[1].x * vector.y + matrix[2].x * vector.z + matrix[3].x * vector.w;
        const y = matrix[0].y * vector.x + matrix[1].y * vector.y + matrix[2].y * vector.z + matrix[3].y * vector.w;
        const z = matrix[0].z * vector.x + matrix[1].z * vector.y + matrix[2].z * vector.z + matrix[3].z * vector.w;
        const w = matrix[0].w * vector.x + matrix[1].w * vector.y + matrix[2].w * vector.z + matrix[3].w * vector.w;
        result.x = x;
        result.y = y;
        result.z = z;
        result.w = w;
        return result;
    }
    /**
     * Creates a new `IMatrix4` object from the provided values.
     * @returns Anonymous object `IMatrix4`
     */
    static from_values(m11, m12, m13, m14, m21, m22, m23, m24, m31, m32, m33, m34, m41, m42, m43, m44) {
        const matrix = [
            { x: m11, y: m12, z: m13, w: m14 },
            { x: m21, y: m22, z: m23, w: m24 },
            { x: m31, y: m32, z: m33, w: m34 },
            { x: m41, y: m42, z: m43, w: m44 }
        ];
        return matrix;
    }
    /**
     * Creates a new `IMatrix4` object from the provided values and writes the result to the `result` matrix.
     * @note This method directly modifies the `result` matrix.
     */
    static from_values_to_ref(m11, m12, m13, m14, m21, m22, m23, m24, m31, m32, m33, m34, m41, m42, m43, m44, result) {
        result[0].x = m11;
        result[0].y = m12;
        result[0].z = m13;
        result[0].w = m14;
        result[1].x = m21;
        result[1].y = m22;
        result[1].z = m23;
        result[1].w = m24;
        result[2].x = m31;
        result[2].y = m32;
        result[2].z = m33;
        result[2].w = m34;
        result[3].x = m41;
        result[3].y = m42;
        result[3].z = m43;
        result[3].w = m44;
        return result;
    }
    /**
     * Creates a new `IMatrix4` object from the provided array.
     * @returns Anonymous object `IMatrix4`
     */
    static from_array(array, offset = 0) {
        return [
            { x: array[offset], y: array[offset + 1], z: array[offset + 2], w: array[offset + 3] },
            { x: array[offset + 4], y: array[offset + 5], z: array[offset + 6], w: array[offset + 7] },
            { x: array[offset + 8], y: array[offset + 9], z: array[offset + 10], w: array[offset + 11] },
            { x: array[offset + 12], y: array[offset + 13], z: array[offset + 14], w: array[offset + 15] }
        ];
    }
    static to_matrix_object(matrix) {
        return {
            m11: matrix[0].x, m12: matrix[0].y, m13: matrix[0].z, m14: matrix[0].w,
            m21: matrix[1].x, m22: matrix[1].y, m23: matrix[1].z, m24: matrix[1].w,
            m31: matrix[2].x, m32: matrix[2].y, m33: matrix[2].z, m34: matrix[2].w,
            m41: matrix[3].x, m42: matrix[3].y, m43: matrix[3].z, m44: matrix[3].w
        };
    }
    static transpose(source) {
        const transposed = new IMatrix4();
        transposed[0].x = source[0].x;
        transposed[0].y = source[1].x;
        transposed[0].z = source[2].x;
        transposed[0].w = source[3].x;
        transposed[1].x = source[0].y;
        transposed[1].y = source[1].y;
        transposed[1].z = source[2].y;
        transposed[1].w = source[3].y;
        transposed[2].x = source[0].z;
        transposed[2].y = source[1].z;
        transposed[2].z = source[2].z;
        transposed[2].w = source[3].z;
        transposed[3].x = source[0].w;
        transposed[3].y = source[1].w;
        transposed[3].z = source[2].w;
        transposed[3].w = source[3].w;
        return transposed;
    }
    /**
     * Creates a matrix positioned at 'position' and oriented towards 'target'.
     * The up vector is aligned to the plane along 'approximate_up' and the calculated forward vector.
     */
    static look_at(position, target, approximate_up) {
        const camera = new IMatrix4();
        const forward = IVector3.normalize(IVector3.subtract(target, position));
        const right = IVector3.normalize(IVector3.cross(forward, approximate_up));
        const up = IVector3.cross(right, forward);
        camera[0] = new IVector4(right.x, right.y, right.z, 0);
        camera[1] = new IVector4(up.x, up.y, up.z, 0);
        camera[2] = new IVector4(forward.x, forward.y, forward.z, 0);
        camera[3] = new IVector4(position.x, position.y, position.z, 1);
        return camera;
    }
}
//# sourceMappingURL=IMatrix4.js.map